import React, { FC, useEffect, useMemo, useState } from 'react';
import { BookingInterface, DeliveryType, DPDPointDeliveryType, extraItemBookingInterface, extraItemsInterface, StepProps } from "../types/types";
import ExtraItemCard from "./business/ExtraItemCard";
import { ExtraItemConfig, extraItemsConfig, getBookingConfig, logError } from "../data/price";
import BookingService from "../api/BookingService";
import { useTranslation } from "react-i18next";
import LoadingIndicator from "./utils/LoadingIndicator";
import { RentalPointName } from "../data/RentalPointName";
import { DeliveryManager } from '../data/DeliveryManager';
import { useAppDispatch, useAppSelector } from '../hooks';
import { setExtraQuantity, setExtras, setFreeDeliverySwitch } from '../stateReducers/extraItemsSlice';
import { autoExtras, extraArrayToDict, flattenObjectOneLevel, isBow, isFreeDelivery } from './utils/stateHelper';
import { setStep } from '../stateReducers/globalSlice';


const Step4: FC<StepProps> = ({setStepValidity}) => {
    const {t} = useTranslation();
    const d = useAppDispatch();
    const booking = useAppSelector((state) => { return flattenObjectOneLevel(state) as BookingInterface; });
    const extras = useAppSelector((state) => { return state.extraItems; });
    const deliveryOptions = useAppSelector((state) => { return state.delivery; });
    const gunOptions = useAppSelector((state) => { return state.gunOptions; });
    const formType = useAppSelector((state) => { return state.delivery.formType; });
    const maxStepReached = useAppSelector((state) => { return state.globals.maxStepReached; });
    const lang = useAppSelector((state) => { return state.globals.lang; });
    
    const [showPromptForFreeSwitch, setShowPromptForFreeSwitch] = useState<boolean>(false);
    const modalClassName = showPromptForFreeSwitch ? "modal-background" : "hidden";

    useEffect(() => {
        // let isValid = extra.items.every((item) => {
        //     return item.type === 'anticheats' || item.quantity > 0;
        // })
        setStepValidity(true);
    }, [extras.items]);

    useEffect(() => {
        if (isFreeDelivery(deliveryOptions.deliveryType! as DeliveryType) && isBow(gunOptions.gunType!)) {
            setShowPromptForFreeSwitch(true);
        }
    }, [deliveryOptions])

    const deliveryAllowsAntiCheatSelection = () => {
        //rental points except Hasselt and Herent always include anti-cheat, so selection is disabled
        return !(deliveryOptions.deliveryType === 'rental' && [RentalPointName.MERKSEM, RentalPointName.GHENT, RentalPointName.BRUGGE, RentalPointName.BEERSE, RentalPointName.AALST].includes(deliveryOptions.rentalPoint || 0));
    }

    const setExtraItemQuantity = (type: string, quantity: number) => {
        d(setExtraQuantity({
            type: type,
            quantity: quantity
        }));
    }

    const getExtraItemQuantity = (slug: string) => {
        let booked = extras.items.filter((item) => item.type === slug);
        if (booked.length) {
            return booked[0].quantity
        }

        if (slug === 'anticheats') {
            console.log('getExtraItemQuantity anticheats', booked, gunOptions.quantity);
            if (!deliveryAllowsAntiCheatSelection()) {
                return 1;
            } else {
                if (gunOptions.quantity && booked.length >= gunOptions.quantity) {
                    return 1;
                }
            }
        }

        return 0;
    }
    const toggleItem = (slug: string, selected: boolean) => {
        if (!selected) {
            d(setExtras(
                {
                    items: extras.items.filter((item) => {
                        return item.type !== slug
                    })
                }
            ));
        }
    }

    const fetched = () => {
        if (extras.items.length !== 0 && maxStepReached > 4) {
            return true;
        } else {
            return false;
        }
    }
    const [extraItemsQuantityFetched, setExtraItemsQuantityFetched] = useState(fetched());

    /**
     * Do all filtering that can be done before the server
     */
    const initAvailableExtraItems = () => {
        return extraItemsConfig
        // Exclude objects from non-rental
        .filter((item) => {
            if (deliveryOptions.deliveryType === 'rental') {
                return true;
            }
            //inflatable objects are only available in rental points
            return item.slug !== 'objects';
        })
        .filter((item) => {
            // Filter out barrels for DPD point deliveries
            if (
                item.slug === 'barrels'
                // @ts-ignore
                && Object.values(DPDPointDeliveryType).includes(deliveryOptions.deliveryType ?? "")
            ) {
                return false;
            } else {
                return true;
            }
        })
        // Filter based on gunType
        .filter((item) => {
            if (gunOptions.gunType === 'phoenix') {
                const itemsToDisplayForPhoenix = [
                    'camouflage',
                    'charger',
                    'barrels',
                    'blasts',
                    'telescope',
                    'falcon',
                    'anticheats',
                    'buffs',
                ]
                if (deliveryOptions.deliveryType === 'rental') {
                    itemsToDisplayForPhoenix.push('objects');
                }
                return itemsToDisplayForPhoenix.includes(item.slug);
            } else if (gunOptions.gunType === 'brx') {
                const itemsToDisplayForBrx = [
                    'camouflage',
                    'charger',
                    'barrels',
                    'buffs',
                ];
                if (deliveryOptions.deliveryType === 'rental') {
                    itemsToDisplayForBrx.push('objects');
                }
                return itemsToDisplayForBrx.includes(item.slug);
            } else if (isBow(gunOptions.gunType!)) {
                const itemsToDisplayForBows = [
                    'camouflage',
                    'barrels',
                    'extraArrows',
                    'target',
                    'buffs',
                ];
                if (deliveryOptions.deliveryType === 'rental') {
                    itemsToDisplayForBows.push('objects');
                }
                return itemsToDisplayForBows.includes(item.slug);
            }

            return true;
        })
    }

    const [availableExtraItems, setAvailableExtraItems] = useState<ExtraItemConfig[]>(initAvailableExtraItems());

    const getGunPackageQuantity = () => {
        let quantity = gunOptions.quantity ?? 0;
        if ('phoenix' === gunOptions.gunType) {
            return quantity / 8;
        }

        return quantity;
    }

    useEffect(() => {
        // Extras will be wiped in extraItemsSlice if they are invalidated by other steps
        if (extras.items.length === 0) {
            BookingService.checkExtrasAvailability(booking).then((response) => {
                console.log('available', response.data);
                logError('BookingService.checkExtrasAvailability ', {
                    response:response.data,
                    extra:extras
                });
                setExtraItemsQuantityFetched(true);
                const available = availableExtraItems.filter((item) => {
                    if ('anticheats' === item.slug && gunOptions.quantity) {
                        const antiCheatsAvailable = response.data[item.slug] >= getGunPackageQuantity();
                        setCanSelectAntiCheat(deliveryAllowsAntiCheatSelection() && antiCheatsAvailable);
                        // Temporarily, Aalst doesn't have anti-cheat available
                        if (!antiCheatsAvailable || deliveryOptions.rentalPoint === RentalPointName.AALST) {
                            setExtraItemQuantity('anticheats', 0);
                        }
                        return antiCheatsAvailable;
                    }
                    return response.data[item.slug] > 0;
                })
                .map((item) => {
                    let maxDropdown = Math.min(response.data[item.slug], item.maxDropdownLength);
                    if (item.slug === 'telescope') {
                        // Meaning, it's a phoenix order, and getGunPackageQuantity can be used
                        maxDropdown = Math.min(maxDropdown, getGunPackageQuantity());
                    }
                    return {
                        ...item,
                        //for example camouflage stick quantity is 1000000, so use in value not to render 1000000
                        maxDropdownLength: maxDropdown,
                        //for delivery min number of foldable barrels and targets is 2
                        min: ((item.slug === 'barrels' || item.slug === 'target') && deliveryOptions.deliveryType !== 'rental') ? 2 : item.min
                    };
                });
                logError('setAvailableExtraItems ', available);
                setAvailableExtraItems(available);    
            });
        }
    }, [gunOptions.gunType]);

    const [isSelected, setSelected] = useState(getExtraItemQuantity('anticheats') !== 0);
    const handleAntiCheatsClick = (selected: boolean) => {
        if (canSelectAntiCheat) {
            setSelected(selected);
        }
        if (!selected) {
            setExtraItemQuantity('anticheats', 0);
        }
    }

    const checkCanSelectAntiCheat = () => {
        const deliveryAllows = deliveryAllowsAntiCheatSelection();
        // There are currently no points without anti-cheat
        const pointAllows = true;
        return deliveryAllows && pointAllows;
    }
    const [canSelectAntiCheat, setCanSelectAntiCheat] = useState(checkCanSelectAntiCheat());

    useEffect(() => {
        console.log('canSelectAntiCheat', canSelectAntiCheat);
        if (!canSelectAntiCheat) {
            setExtraItemQuantity('anticheats', 1);
            setSelected(true);
        }
    }, []);


    function calculateFreeSets() {
        const newExtras = autoExtras(deliveryOptions, formType, gunOptions, extras, lang);
        console.log("Available extras", availableExtraItems);
        const slugs: string[] = availableExtraItems.map((item) => {
            return item.slug;
        });
        const extraItems: extraItemBookingInterface[] = newExtras.filter(
            // Filter out unavailable items
            (obj) => {
                if (slugs.includes(obj.type)) {
                    return true;
                } else {
                    return false;
                }
            }
        );
        console.log("Auto extras - post-availability", extraItems);
        if (extraItems.length !== 0) {
            // Only overwrite extras if there wasn't anything before
            if (extras.items.length === 0) {
                d(setExtras({items: extraItems}));
            }
            return extraArrayToDict(extraItems);
        } else {
            return null;
        }
    }
    // Will be calculated once per time Step4 is loaded, which is fine
    const [freeSetsDict, setFreeSetsDict] = useState<{[item: string]: number} | null>(calculateFreeSets());

    useEffect(() => {
        // If extras have been cleared or if it's the first time
        if (extras.items.length === 0) {
            
        }
    },[gunOptions, deliveryOptions, formType]);

    useEffect(() => {
        setTimeout(() => {
            const top = document.getElementById('root')?.getBoundingClientRect().top ?? 0;
            window.scrollTo(0, top + window.scrollY);
        }, 200);
    }, [extraItemsQuantityFetched]);

    function activateFreeDeliverySwitch() {
        setShowPromptForFreeSwitch(false);
        d(setFreeDeliverySwitch(true));
        calculateFreeSets();
        d(setStep(2));
    }

    const bookingConfig = getBookingConfig();

    return (
        <>
        {/* Modal box to potentially pop up and ask the client about switching to pickup for a promotion */}
        {/* <div className={modalClassName} onClick={() => setShowPromptForFreeSwitch(false)}> */}
        <div className={modalClassName}>
            <div className="promo-box" onClick={() => {return} }>
                <img className="promo-banner-img" src={bookingConfig.inflatableObjectsAltImage} alt="" />
                <p dangerouslySetInnerHTML={{__html: t('promo.freeDeliverySwitch', {interpolation: {escapeValue: false}})}}></p>
                <span
                    className='close close-green'
                    onClick={() => { activateFreeDeliverySwitch() }}
                >{t('promo.activateButton')}</span>
                <span
                    className='close close-red'
                    onClick={() => { setShowPromptForFreeSwitch(false); }}
                >{t('promo.notActivateButton')}</span>
            </div>
        </div>

        <div className="flex flex-col mb-6">
            {!extraItemsQuantityFetched &&
                <LoadingIndicator text={t("loadingExtraItemsQuantity")}/>
            }
            {extraItemsQuantityFetched && <div className="m-2 lg:m-6 mb-3 w-full">
                <h3>{t("extraLabel")}</h3>
            </div>
                }
            {extraItemsQuantityFetched && gunOptions.gunType === 'phoenix' && <div className="extra-item-row">
                <div className="extra-item-box"
                     onClick={(e) => handleAntiCheatsClick(!isSelected)}>
                    <div className='extra-item-image-div'>
                        <img src='https://www.lazertag.be/wp-content/uploads/2019/10/afstandsbedieningen.jpg' alt={t("extra.anticheats.title")} />
                    </div>
                    <div
                        className={"text-center h-10 bg-gradient-to-b from-transparent to-orange-300 rounded" + (isSelected ? ' shadow-lg shadow-amber-600' : '')}>
                    <span
                        className="pt-2 inline-block align-text-bottom font-bold">{t('extra.anticheats.title')}</span>
                    </div>
                </div>
                <div className="w-full lg:w-1/3 p-2 lg:p-6">
                    <div>{t('extra.anticheats.description')}</div>
                    <b>{t('extra.anticheats.priceText')}</b>
                    <div>{t('extra.anticheats.price')}</div>
                </div>
                <div className="w-full lg:w-1/3 p-2 lg:p-6">
                    <div>{t('extra.anticheats.dropdownLabel')}</div>
                    <div className="flex justify-center flex-col">
                        <div className="mb-3 xl:w-full">
                            <div className="form-check">
                                <label className="form-check-label inline-block text-gray-800" htmlFor="anticheats_yes">
                                    <input
                                        disabled={!canSelectAntiCheat}
                                        className="form-check-input  rounded-full h-4 w-4 border border-gray-300 bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer bg-gray-300"
                                        type="radio" value={1} name="anticheats"
                                        checked={getExtraItemQuantity('anticheats') === 1}
                                        onChange={(e) => setExtraItemQuantity('anticheats', 1)}
                                        id="anticheats_yes"/> {t('extra.anticheats.yesText')}
                                </label>
                            </div>
                            <div className="form-check">
                                <label className="form-check-label inline-block text-gray-800" htmlFor="anticheats_no">
                                    <input
                                        disabled={!canSelectAntiCheat}
                                        className="form-check-input  rounded-full h-4 w-4 border border-gray-300 bg-white checked:bg-blue-600 checked:border-blue-600 focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer bg-gray-300"
                                        type="radio" value={0} name="anticheats"
                                        checked={getExtraItemQuantity('anticheats') === 0}
                                        onChange={(e) => setExtraItemQuantity('anticheats', 0)}
                                        id="anticheats_no"/> {t('extra.anticheats.noText')}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            }
            {extraItemsQuantityFetched && availableExtraItems.map((item, index) => {
                if (item.slug === 'anticheats') {
                    return "";
                }
                let numFree = 0;
                if (freeSetsDict && item.slug in freeSetsDict) {
                    numFree = freeSetsDict[item.slug];
                }
                return <ExtraItemCard key={item.slug}
                                      setExtraItemQuantity={(quantity) => setExtraItemQuantity(item.slug, quantity)}
                                      extraItemQuantity={getExtraItemQuantity(item.slug)}
                                      onClick={toggleItem}
                                      {...item}
                                      formType={formType}
                                      numFree={numFree} />
            })}
        </div>
        </>
    );
};

export default Step4;
